import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import {
  DataEntity,
  DataEntityDto,
  DataEntityFieldsControllerService,
  DataReactionControllerService,
} from '@osos/shared/api';
import { isSameOrAfterDate } from '@osos/shared/legacy-common';
import {
  ApplicationSharingLevelEnum,
  BreadcrumbToken,
  DataEntityPresenterModel,
  DataEntityStateEnum,
  DataEntityTypeEnum,
  DataEntityVersionChangesPresenterModel,
  DataEntryPresenterModel,
  DataReactionWithRelationsPresenterModel,
  DEFAULT_PAGESIZE,
  FieldPresenterModel,
  GROUP_ENTITY_ID,
  LoadingIndicator,
  LoadingType,
  ManagedHttpContext,
  Page,
  USER_ENTITY_ID,
  Where,
} from '@osos/shared/types';
import { EMPTY, Observable, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import {
  DataReactionWithRelationsFactoryService,
  EntityVersionChangesFactoryService,
  FieldFactoryService,
} from '../../factories';
import { EntityFactoryService } from '../../factories/entity-factory/entity-factory.service';
import {
  ApiUtilsService,
  BreadcrumbService,
  EntityDataService,
} from '../../services';
import { isVirtualJoinerEntity, isVirtualProxyEntity } from '../../utils';
import { AppStateActions } from '../app/app.actions';
import { EntryActions } from '../entry-data-state/entry.action';
import { LoadingActions } from '../loading/loading.actions';
import { ModuleActions } from '../modules-data-state/module.action';
import { EntityActions } from './entity.action';

export interface EntityStateModel {
  page?: Page<DataEntity>;
  presentedEntities: DataEntityPresenterModel[];
  presentedEntity: { [key: string]: DataEntityPresenterModel };
  selectedEntity?: string;
  lastCreatedEntity?: string;
  totalCount?: number;
  selectedEntriesForPresentedEntity?: DataEntryPresenterModel[];

  /**
   * temporary state to store entities and select them once
   */
  transitoryEntities?: DataEntityPresenterModel[];
  transitoryEntitiesWithMainPhone: Array<string>;
  loading: LoadingIndicator;
  systemFields?: FieldPresenterModel[];
  entityVersionChanges: DataEntityVersionChangesPresenterModel[];
  queryRelationFieldsMappingByFieldPath: {
    fieldName: string;
    field: FieldPresenterModel;
  }[];
  presentedReactions?: {
    [key: string]: DataReactionWithRelationsPresenterModel[];
  };
}

@State<EntityStateModel>({
  name: 'entities',
  defaults: {
    page: {
      items: [],
      totalCount: 0,
    },
    presentedEntity: {},
    presentedEntities: [],
    transitoryEntities: [],
    selectedEntriesForPresentedEntity: [],
    transitoryEntitiesWithMainPhone: [],
    entityVersionChanges: [],
    loading: new LoadingIndicator(),
    queryRelationFieldsMappingByFieldPath: [],
    presentedReactions: {},
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class EntityState {
  private requestsInProgress = {};

  constructor(
    private entityService: EntityDataService,
    private apiHelper: ApiUtilsService,
    private entityFactoryService: EntityFactoryService,
    private breadcrumbService: BreadcrumbService,
    private entityFieldService: DataEntityFieldsControllerService,
    private fieldFactory: FieldFactoryService,
    private entityChangesFactory: EntityVersionChangesFactoryService,
    private store: Store,
    private reactionControllerService: DataReactionControllerService,
    private reactionFactoryService: DataReactionWithRelationsFactoryService,
  ) {}

  // --------------------------------- SELECTORS ------------------------------
  @Selector([EntityState])
  static totalCount(state: EntityStateModel): number {
    return state.page.totalCount;
  }

  @Selector([EntityState])
  static presentedEntities(
    state: EntityStateModel,
  ): DataEntityPresenterModel[] {
    return state.presentedEntities;
  }

  @Selector([EntityState])
  static selectedEntriesForPresentedEntity(
    state: EntityStateModel,
  ): DataEntryPresenterModel[] {
    return state.selectedEntriesForPresentedEntity;
  }

  @Selector([EntityState])
  static lastCreatedEntity(state: EntityStateModel): DataEntityPresenterModel {
    return state.presentedEntity[state.lastCreatedEntity];
  }

  @Selector([EntityState])
  static selectedEntity(state: EntityStateModel): DataEntityPresenterModel {
    return state.presentedEntity[state.selectedEntity];
  }

  static presentedEntity(
    id: string,
  ): (state: EntityStateModel) => DataEntityPresenterModel {
    return createSelector(
      [EntityState],
      (state: EntityStateModel) => state.presentedEntity[id],
    );
  }

  @Selector([EntityState])
  static entitiesCount(state: EntityStateModel): number {
    return state.totalCount;
  }
  @Selector()
  static queryFieldsMappingByFieldPath(state: EntityStateModel) {
    return state.queryRelationFieldsMappingByFieldPath;
  }
  /**
   * select transitory entities.
   * it's valid to be read once after trigger TransitoryFetchEntities action
   */
  @Selector([EntityState])
  static transitoryEntities(
    state: EntityStateModel,
  ): DataEntityPresenterModel[] {
    return state.transitoryEntities;
  }

  @Selector([EntityState])
  static transitoryEntitiesWithMainPhone(state: EntityStateModel): string[] {
    return state.transitoryEntitiesWithMainPhone;
  }

  static loading(type: LoadingType): (state: EntityStateModel) => boolean {
    return createSelector(
      [EntityState],
      (state: EntityStateModel) => state.loading[type],
    );
  }

  @Selector([EntityState])
  static loadingIndicator(state: EntityStateModel): LoadingIndicator {
    return state.loading;
  }

  @Selector([EntityState])
  static systemFields(state: EntityStateModel): FieldPresenterModel[] {
    return state.systemFields;
  }

  @Selector([EntityState])
  static entityVersionChanges(
    state: EntityStateModel,
  ): DataEntityVersionChangesPresenterModel[] {
    return state.entityVersionChanges;
  }

  static presentedReactions(
    entityId: string,
  ): (state: EntityStateModel) => DataReactionWithRelationsPresenterModel[] {
    return createSelector(
      [EntityState],
      (state: EntityStateModel) => state.presentedReactions[entityId],
    );
  }
  // --------------------------------- ACTIONS --------------------------------
  @Action(EntityActions.FetchEntities, { cancelUncompleted: true })
  fetchEntities(
    { setState, dispatch }: StateContext<EntityStateModel>,
    { filter, skip, limit, searchTerm, moduleId }: EntityActions.FetchEntities,
  ): Observable<Page<DataEntity>> {
    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<EntityStateModel>({
        presentedEntities: [],
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    const where: Where<DataEntityDto> = {
      state: filter?.isArchived
        ? { eq: DataEntityStateEnum.Archived }
        : { neq: DataEntityStateEnum.Archived },
      type: { neq: DataEntityTypeEnum.ADT },
      module: (filter?.module || moduleId) ?? undefined,
      //  entriesCount: filter?.entriesCount ?? undefined,
      or: this.apiHelper.prepareSearch(searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
    };

    return this.entityService
      .find(
        this.apiHelper.prepareFilter<DataEntity>({
          where,
          skip: skip ?? 0,
          limit: limit ?? DEFAULT_PAGESIZE,
          order: ['updateInfo.doneAt DESC'],
          fields: [
            'id',
            'name',
            'normalizedName',
            'type',
            'module',
            'state',
            'icon',
            'displayOption',
            'entryNames',
            'description',
          ],
        }),
      )
      .pipe(
        switchMap((elements) =>
          this.entityService
            .count(this.apiHelper.prepareWhere<DataEntity>(where))
            .pipe(
              map((count) => {
                return {
                  items: elements,
                  totalCount: count.count,
                } as Page<DataEntity>;
              }),
            ),
        ),
        tap((page) => {
          const presentedEntities = page.items.map((x) =>
            this.entityFactoryService.createPresenterModel(x),
          );
          setState(
            patch<EntityStateModel>({
              page,
              presentedEntities,
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetLoading(false));
          setState(
            patch<EntityStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntityActions.TransitoryFetchEntitiesWithMainPhone)
  transitoryFetchEntitiesWithMainPhone({
    setState,
  }: StateContext<EntityStateModel>): Observable<any> {
    const where: Where<any> = {
      or: [
        {
          'fields.options.showAsMainPhone': true,
        },
        {
          'internalEntities.fields.options.showAsMainPhone': true,
        },
      ],
    };

    return this.entityService
      .find(
        this.apiHelper.prepareFilter<DataEntity>({
          where,
          fields: { id: true },
        }),
      )
      .pipe(
        tap((res) => {
          const mainPhoneIds = res.map((item) => item.id);
          const mainPhoneList = mainPhoneIds.includes(USER_ENTITY_ID)
            ? [...mainPhoneIds, GROUP_ENTITY_ID]
            : mainPhoneIds;
          setState(
            patch<EntityStateModel>({
              transitoryEntitiesWithMainPhone: mainPhoneList,
            }),
          );
        }),
      );
  }

  @Action(EntityActions.TransitoryFetchEntities)
  transitoryFetchEntities(
    { setState }: StateContext<EntityStateModel>,
    { payload }: EntityActions.TransitoryFetchEntities,
  ): Observable<any> {
    const isArchived = payload?.filter?.isArchived ?? false;
    const where: Where<DataEntityDto> = {
      id: payload?.entityIds?.length
        ? { inq: payload.entityIds }
        : payload?.excludedIds?.length
          ? { nin: payload?.excludedIds }
          : undefined,
      normalizedName: payload?.filter?.normalizedName,
      state: isArchived
        ? { eq: DataEntityStateEnum.Archived }
        : { neq: DataEntityStateEnum.Archived },
      type: { neq: DataEntityTypeEnum.ADT },
      module: (payload?.filter?.module || payload.moduleId) ?? undefined,
      //  entriesCount: filter?.entriesCount ?? undefined,
      and: [
        payload.filter?.isTimeline
          ? ({
              'extentions.timelineContribution.enabled': true,
              ...(payload.filter?.timeLineCategory && {
                'extentions.timelineContribution.category.dataEntry':
                  payload.filter?.timeLineCategory,
              }),
            } as any)
          : {},
      ],
      or: this.apiHelper.prepareSearch(payload.searchTerm, [
        'name.en',
        'name.ar',
        'description.en',
        'description.ar',
      ]),
    };

    if (payload?.isEntityPublic) {
      where['sharing.level.id'] = ApplicationSharingLevelEnum.Public;
    }

    const includeFields: { fields?: (keyof DataEntity)[] } =
      payload.getFullEntityDetails
        ? {}
        : {
            fields: [
              'id',
              'name',
              'normalizedName',
              'type',
              'module',
              'state',
              'icon',
              'displayOption',
              'entryNames',
              'description',
            ],
          };

    return this.entityService
      .find(
        this.apiHelper.prepareFilter<DataEntity>({
          where,
          skip: payload.skip ?? 0,
          limit: payload.limit ?? 20,
          order: ['updateInfo.doneAt DESC'],
          ...includeFields,
        }),
      )
      .pipe(
        tap((res) => {
          setState(
            patch<EntityStateModel>({
              transitoryEntities: res.map((item) =>
                this.entityFactoryService.createPresenterModel(item),
              ),
            }),
          );
        }),
      );
  }

  @Action(EntityActions.FetchEntity)
  fetchEntity(
    { setState, getState }: StateContext<EntityStateModel>,
    { entityId, viewId }: EntityActions.FetchEntity,
  ): Observable<any> {
    // if (UNSTRUCTURED_ENTITIES_ID.includes(entityId)) {
    //   return EMPTY;
    // }
    if (!entityId) {
      return EMPTY;
    }
    const entity = getState().presentedEntity[entityId];
    if (entity) {
      return of(entity);
    }
    if (this.requestsInProgress[entityId]) {
      return EMPTY;
    }
    this.requestsInProgress[entityId] = true;
    setState(
      patch<EntityStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.entityService
      .findById({
        id: entityId,
        context: viewId
          ? new ManagedHttpContext({ publicAccessId: viewId })
          : undefined,
      })
      .pipe(
        tap((res) => {
          const presenter = this.entityFactoryService.createPresenterModel(res);
          setState(
            patch<EntityStateModel>({
              presentedEntity: patch({
                [entityId]: presenter,
              }),
            }),
          );
        }),
        finalize(() => {
          this.requestsInProgress[entityId] = false;

          setState(
            patch<EntityStateModel>({
              loading: patch<LoadingIndicator>({
                item: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(EntityActions.SelectEntity)
  selectEntity(
    { getState, dispatch, patchState }: StateContext<EntityStateModel>,
    { payload }: EntityActions.SelectEntity,
  ): Observable<any> | null {
    const { entityId, resolveEntryBreadcrumbTokens } = payload;
    const prevSelectedEntity = getState().selectedEntity;
    const selectedEntity = getState().presentedEntity[entityId];

    patchState({
      selectedEntity: entityId,
    });

    if (!selectedEntity) {
      return null;
    }

    if (prevSelectedEntity && prevSelectedEntity !== selectedEntity.id) {
      this.store.dispatch(
        new EntryActions.ClearEntityPresentedEntries(selectedEntity.id),
      );
      patchState({
        selectedEntriesForPresentedEntity: [],
      });
    }

    // Resolve breadcrumb tokens:
    this.breadcrumbService.set(BreadcrumbToken.EntityName, selectedEntity.name);

    if (resolveEntryBreadcrumbTokens) {
      // TODO: Change the following to a dynamic solution:
      const singular = {
        en: selectedEntity.entryNames?.singular?.en ?? 'Entry',
        ar: selectedEntity.entryNames?.singular?.ar ?? 'مدخل',
      };

      this.breadcrumbService.set(BreadcrumbToken.EntryForm, {
        en: `${singular.en} Form`,
        ar: `إضافة ${singular.ar}`,
      });
      this.breadcrumbService.set(BreadcrumbToken.ViewEntry, {
        en: `View ${singular.en}`,
        ar: `عرض ${singular.ar}`,
      });
      this.breadcrumbService.set(BreadcrumbToken.EditEntry, {
        en: `Edit ${singular.en}`,
        ar: `تعديل ${singular.ar}`,
      });
    }

    // Clear entry applied filter
    dispatch(new EntryActions.ClearAppliedFilter());
    // Select the entity's module as the current module:
    return dispatch(new ModuleActions.FetchModule(selectedEntity.module)).pipe(
      tap(() =>
        dispatch(new ModuleActions.SelectModule(selectedEntity.module)),
      ),
    );
  }

  @Action(EntityActions.UpdateEntitySelectedEntries)
  updateSelectedEntitySelectedEntries(
    { patchState }: StateContext<EntityStateModel>,
    { payload }: EntityActions.UpdateEntitySelectedEntries,
  ) {
    const { selectedEntries } = payload;

    patchState({
      selectedEntriesForPresentedEntity: selectedEntries,
    });
  }

  @Action(EntityActions.CreateEntity)
  createEntity(
    ctx: StateContext<EntityStateModel>,
    { payload }: EntityActions.CreateEntity,
  ): Observable<DataEntity> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));

    ctx.setState(
      patch<EntityStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );
    const model = this.entityFactoryService.createDataModel(payload);
    return (
      isVirtualProxyEntity(payload) || isVirtualJoinerEntity(payload)
        ? this._createProxyEntity(model, payload)
        : this._createNormalEntity(model)
    ).pipe(
      tap((res) => {
        const presenter = this.entityFactoryService.createPresenterModel(res);
        ctx.setState(
          patch<EntityStateModel>({
            presentedEntities: append([presenter]),
            presentedEntity: patch({
              [presenter.id]: presenter,
            }),
            lastCreatedEntity: presenter.id,
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetCommandLoading(false));

        ctx.setState(
          patch<EntityStateModel>({
            loading: patch<LoadingIndicator>({
              create: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(EntityActions.UpdateEntity)
  updateEntity(
    ctx: StateContext<EntityStateModel>,
    { payload }: EntityActions.UpdateEntity,
  ): Observable<any> {
    ctx.dispatch(new LoadingActions.SetCommandLoading(true));

    ctx.setState(
      patch<EntityStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );

    const prevEntity = ctx.getState().presentedEntity[payload.id];
    const entity = this.entityFactoryService.createDataModel(payload);
    entity.module = undefined;

    return (
      isVirtualProxyEntity(payload) || isVirtualJoinerEntity(payload)
        ? this._updateProxyEntity(entity, payload)
        : this._updateNormalEntity(entity, payload)
    ).pipe(
      switchMap(() => this.entityService.findById({ id: payload.id })),
      tap((res) => {
        const presenter = this.entityFactoryService.createPresenterModel(res);
        if (presenter.version > prevEntity.version) {
          this.store.dispatch(new EntryActions.ClearPresentedEntries());
        }
        ctx.setState(
          patch<EntityStateModel>({
            presentedEntities: updateItem(
              (item) => item.id === payload.id,
              presenter,
            ),
            presentedEntity: patch({
              [payload.id]: presenter,
            }),
          }),
        );
      }),
      finalize(() => {
        ctx.dispatch(new LoadingActions.SetCommandLoading(false));

        ctx.setState(
          patch<EntityStateModel>({
            loading: patch<LoadingIndicator>({
              update: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(EntityActions.ArchiveEntity)
  archiveEntity(
    { setState, dispatch }: StateContext<EntityStateModel>,
    { entityId }: EntityActions.ArchiveEntity,
  ): Observable<DataEntityDto> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<EntityStateModel>({
        loading: patch<LoadingIndicator>({
          remove: true,
          update: true,
        }),
      }),
    );
    return this.entityService.deleteById({ id: entityId }).pipe(
      switchMap(() => this.entityService.findById({ id: entityId })),
      tap((res) => {
        const presenter = this.entityFactoryService.createPresenterModel(res);
        setState(
          patch<EntityStateModel>({
            presentedEntities: updateItem((i) => i.id === entityId, presenter),
            presentedEntity: patch({
              [entityId]: presenter,
            }),
          }),
        );
      }),
      finalize(() => {
        dispatch(new LoadingActions.SetCommandLoading(false));

        setState(
          patch<EntityStateModel>({
            loading: patch<LoadingIndicator>({
              remove: false,
              update: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(EntityActions.EntitiesCount)
  EntitiesCount(
    { setState, dispatch }: StateContext<EntityStateModel>,
    { moduleId, dataCollectionId }: EntityActions.EntitiesCount,
  ): Observable<any> {
    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<EntityStateModel>({
        totalCount: 0,
        loading: patch<LoadingIndicator>({
          count: true,
        }),
      }),
    );
    return this.entityService
      .count(
        this.apiHelper.prepareWhere<DataEntity | any>({
          type: { neq: DataEntityTypeEnum.ADT },
          module: moduleId ?? undefined,
          state: { neq: DataEntityStateEnum.Archived },
          'fields.dataCollection': dataCollectionId ?? undefined,
        }),
      )
      .pipe(
        tap((res) => {
          setState(
            patch<EntityStateModel>({
              totalCount: res.count,
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<EntityStateModel>({
              loading: patch<LoadingIndicator>({
                count: false,
              }),
            }),
          );
          dispatch(new LoadingActions.SetLoading(false));
        }),
      );
  }

  @Action(AppStateActions.AppContextChanged)
  clearSelectedEntityOnSwitchAppContext({
    patchState,
  }: StateContext<EntityStateModel>) {
    patchState({
      selectedEntity: undefined,
      selectedEntriesForPresentedEntity: [],
    });
  }

  @Action(EntityActions.FetchSystemFields)
  fetchSystemFields({
    setState,
    getState,
    dispatch,
  }: StateContext<EntityStateModel>): Observable<FieldPresenterModel[]> {
    const systemFields = getState().systemFields;
    if (systemFields?.length) {
      return EMPTY;
    }

    dispatch(new LoadingActions.SetLoading(true));

    setState(
      patch<EntityStateModel>({
        systemFields: [],
      }),
    );
    return this.entityFieldService.findSystemFields().pipe(
      map((res) =>
        res?.map((field) => this.fieldFactory.createPresenterModel(field)),
      ),
      tap((res) => {
        setState(
          patch<EntityStateModel>({
            systemFields: res,
          }),
        );
      }),
      finalize(() => {
        dispatch(new LoadingActions.SetLoading(false));
      }),
    );
  }

  @Action(EntityActions.FetchEntityVersionChanges)
  fetchEntityVersionChanges(
    { setState, dispatch }: StateContext<EntityStateModel>,
    { entity, version }: EntityActions.FetchEntityVersionChanges,
  ): Observable<DataEntityVersionChangesPresenterModel[]> {
    dispatch(new LoadingActions.SetLoading(true));

    setState(
      patch<EntityStateModel>({
        entityVersionChanges: [],
      }),
    );
    return this.entityService
      .getEntityVersionChanges({
        entity,
        version,
      })
      .pipe(
        map((res) =>
          res?.map((x) => this.entityChangesFactory.createPresenterModel(x)),
        ),
        tap((res) => {
          setState(
            patch<EntityStateModel>({
              entityVersionChanges: res,
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetLoading(false));
        }),
      );
  }

  @Action(EntityActions.SetRelationFieldsMapping)
  setQueryFieldsMapping(
    ctx: StateContext<EntityStateModel>,
    { payload }: EntityActions.SetRelationFieldsMapping,
  ) {
    const existingMapping =
      ctx.getState().queryRelationFieldsMappingByFieldPath;
    const newMapping = payload.filter(
      (p) => !existingMapping.some((e) => e.fieldName === p.fieldName),
    );
    const updatedMapping = [...existingMapping, ...newMapping];

    ctx.patchState({
      queryRelationFieldsMappingByFieldPath: updatedMapping,
    });
  }

  @Action(EntityActions.FetchReactionsEntries, {
    cancelUncompleted: true,
  })
  fetchReactions(
    { setState, dispatch }: StateContext<EntityStateModel>,
    { entityId }: EntityActions.FetchReactionsEntries,
  ): Observable<DataReactionWithRelationsPresenterModel[]> {
    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<EntityStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    return this.reactionControllerService.find({ entity: entityId }).pipe(
      map((Reactions) => {
        const sorting = (a, b) => {
          return isSameOrAfterDate(b.updateInfo.doneAt, a.updateInfo.doneAt)
            ? 1
            : -1;
        };
        return Reactions.sort(sorting).map((reaction) =>
          this.reactionFactoryService.createPresenterModel(reaction),
        );
      }),
      tap((presentedReactions: DataReactionWithRelationsPresenterModel[]) => {
        setState(
          patch<EntityStateModel>({
            presentedReactions: patch({
              [entityId]: presentedReactions,
            }),
          }),
        );
      }),
      finalize(() => {
        dispatch(new LoadingActions.SetLoading(false));
        setState(
          patch<EntityStateModel>({
            loading: patch<LoadingIndicator>({
              page: false,
            }),
          }),
        );
      }),
    );
  }
  private _createNormalEntity(entity: DataEntity) {
    return this.entityService.create({
      body: entity,
    });
  }

  private _createProxyEntity(
    entity: DataEntity,
    payload: DataEntityPresenterModel,
  ) {
    return this.entityService
      .createVirtual({
        body: {
          displayOption: entity.displayOption,
          entryNames: entity.entryNames,
          icon: entity.icon,
          module: entity.module,
          name: entity.name,
          normalizedName: entity.normalizedName,
          type:
            entity.type == DataEntityTypeEnum.VirtualProxy
              ? DataEntityTypeEnum.VirtualProxy
              : DataEntityTypeEnum.JoinerVirtualProxy,
          description: entity.description,
          permissions: payload.permissions,
          typeOptions: entity.typeOptions,
        },
      })
      .pipe(switchMap((res) => this.entityService.findById({ id: res.id })));
  }

  private _updateNormalEntity(
    entity: DataEntity,
    payload: DataEntityPresenterModel,
  ) {
    return this.entityService.updateById({
      id: payload.id,
      body: entity,
    });
  }

  private _updateProxyEntity(
    entity: DataEntity,
    payload: DataEntityPresenterModel,
  ) {
    return this.entityService
      .updateVirtualEntityById({
        id: payload.id,
        body: {
          displayOption: entity.displayOption,
          entryNames: entity.entryNames,
          icon: entity.icon,
          name: entity.name,
          description: entity.description,
          permissions: payload.permissions,
          typeOptions: entity.typeOptions,
          fields: entity.fields,
          internalEntities: entity.internalEntities,
          // triggers: entity.triggers,
          templates: entity.templates,
          updateIdentifier: entity.updateIdentifier,
        },
      })
      .pipe(switchMap((res) => this.entityService.findById({ id: res.id })));
  }
}
